<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import type { DraftInvoice } from '@/types'
import { initYandexMetrika } from '@/helpers/yandexMetrika'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiProductCard from '@/components/UiProductCard/UiProductCard.vue'
import UiAntForm from '@/components/UiForm/UiAntForm.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import UiHero from '@/components/UiHero/UiHero.vue'

const route = useRoute()
const unavailable = ref<boolean>(false)
const draftInvoice = ref<DraftInvoice | null>()
const pageLoader = ref(true)

async function getPayment(uuid: string | string[]) {
  try {
    await axios.get(`/d/${uuid}`).then((response: { data: DraftInvoice }) => {
      draftInvoice.value = response.data

      // Инициализация Яндекс.Метрики если передан код счётчика
      if (draftInvoice.value.yandex_metrika_id) {
        initYandexMetrika(draftInvoice.value.yandex_metrika_id)
      }
    })
  } catch (error) {
    unavailable.value = true
  }
}

const offerUrls = computed<string[]>(() => {
  const offers: string[] = []

  if (!draftInvoice.value) {
    return offers
  }

  for (const item of draftInvoice.value?.items) {
    if (item.offer_url) {
      offers.push(item.offer_url)
    } else if (item.legal_entity.offer) {
      offers.push(item.legal_entity.offer)
    }
  }

  return [...new Set(offers)]
})

onMounted(async () => {
  try {
    await getPayment(route.params.uuid)
  } finally {
    pageLoader.value = false
  }
})
</script>

<template>
  <div v-if="pageLoader || draftInvoice" class="payment-cabinet">
    <div>
      <ui-header
        :logo-src="draftInvoice?.merchant.logo_url ?? undefined"
        :logo-loading="pageLoader"
      />

      <!-- Скелетон страницы -->
      <template v-if="pageLoader">
        <!-- Скелетон баннера (UiHero) -->
        <section class="UiHero container skeleton-hero">
          <div class="skeleton-hero__meta">
            <div class="sk-block sk-block--hero-title" />
            <div class="sk-block sk-block--hero-title-short" />
          </div>
          <div class="sk-block sk-block--hero-price" />
        </section>

        <!-- Скелетон формы (UiAntForm) -->
        <div class="ui-form container skeleton-form">
          <div class="sk-block sk-block--form-title" />

          <div class="skeleton-form__fields">
            <div v-for="i in 5" :key="i" class="skeleton-field">
              <div class="sk-block sk-block--label" />
              <div class="sk-block sk-block--input" />
            </div>
          </div>

          <div class="skeleton-form__checkboxes">
            <div v-for="i in 4" :key="i" class="skeleton-checkbox">
              <div class="sk-block sk-block--checkbox-box" />
              <div class="sk-block sk-block--checkbox-text" />
            </div>
          </div>

          <div class="sk-block sk-block--button" />
        </div>
      </template>

      <template v-if="draftInvoice">
        <ui-product-card
          v-show="!draftInvoice.options?.card_hidden"
          :items="draftInvoice.items"
          :total-price="draftInvoice.price"
          :currency="draftInvoice.currency"
          :show-monthly-payment="draftInvoice.show_monthly_payment"
          :monthly-payment="draftInvoice.monthly_payment"
        />

        <ui-ant-form
          :form-options="draftInvoice.options"
          :offer-urls="offerUrls"
          :show-accept-all="draftInvoice.show_accept_all_terms"
        />

        <ui-footer />
      </template>
    </div>
  </div>

  <div v-if="unavailable" class="payment-cabinet">
    <div class="container">
      <ui-hero title="Страница оплаты недоступна" />
    </div>
  </div>
</template>

<style lang="scss">
@keyframes sk-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.sk-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.5s infinite;
}

// --- Hero ---
.skeleton-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
  }
}

.sk-block--hero-title       { height: 32px; width: 55%; max-width: 440px; border-radius: 10px; }
.sk-block--hero-title-short { height: 20px; width: 35%; max-width: 280px; }
.sk-block--hero-price       { height: 36px; width: 110px; border-radius: 10px; flex-shrink: 0; }

// --- Form ---
.skeleton-form {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__checkboxes {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
}

.skeleton-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sk-block--form-title    { height: 28px; width: 65%; max-width: 380px; border-radius: 8px; }
.sk-block--label         { height: 16px; width: 30%; max-width: 160px; border-radius: 4px; }
.sk-block--input         { height: 44px; width: 100%; border-radius: 10px; }
.sk-block--checkbox-box  { width: 20px; height: 20px; border-radius: 4px; flex-shrink: 0; }
.sk-block--checkbox-text { height: 16px; width: 70%; border-radius: 4px; }
.sk-block--button        { height: 44px; width: 100%; border-radius: 10px; }
</style>
